import httpService from '../httpService';
import { store } from '../../redux/store';
import {
  addCommunity,
  deleteCommunity,
  setSelectedCommunity,
  setSelectedDashboard,
  setState,
  wipeSelectedCommunity,
} from '../../redux/slices/applicationSlice';
import { CreateCommunityForm } from '../../types/forms';
import Community, { CommunityMemberPermissions, MemberCommunityDto, CommunityRole } from '../../types/community';
import { getSelectedCommunity } from '../../hooks/community/useSelectedCommunity';
import { UploadedPictureModel } from '../../models/communityService.model';
import { RootState } from '../../redux/reducers';
import { CommunityFeatures } from '../../utils/FeatureUtils';
import { SubscriptionType } from '../../types/misc';
import { Connection, ConnectionType } from '../../redux/slices/connectionsSlice';

const communityService = {
  /**
   * Sets the user's profile image.
   *
   * @function
   * @async
   * @param {File} image - The image file to set as the user's profile image.
   * @returns {Promise<void>} A Promise that resolves when the profile image has been set.
   */
  setProfileImage: async (image: File, communityId?: number): Promise<void> => {
    const resolved = communityId ?? store.getState().application.selectedCommunity;
    const formData = new FormData();
    formData.append('avatar', image);
    return httpService
      .post(`/networks/communities/${resolved}/profile-picture`, formData)
      .then(({ data }): void => {
        const { application } = store.getState();
        const { communities } = application;
        if (communities?.some((c: Community) => c.id === resolved)) {
          store.dispatch(
            setState({
              ...application,
              communities: communities.map((c: Community) =>
                c.id === resolved ? { ...c, picture: data.path } : c,
              ),
            }),
          );
        }
      });
  },

  /**
   * Deletes the user's profile image.
   *
   * @function
   * @async
   * @returns {Promise<void>} A Promise that resolves when the profile image has been deleted.
   */
  deleteProfileImage: async (communityId?: number): Promise<void> => {
    const resolved = communityId ?? store.getState().application.selectedCommunity;
    return httpService
      .delete(`/networks/communities/${resolved}/profile-picture`)
      .then((): void => {
        const { application } = store.getState();
        const { communities } = application;
        if (communities?.some((c: Community) => c.id === resolved)) {
          store.dispatch(
            setState({
              ...application,
              communities: communities.map((c: Community) =>
                c.id === resolved ? { ...c, picture: null } : c,
              ),
            }),
          );
        }
        window.dispatchEvent(new Event('profileImage'));
      });
  },

  createCommunity: async (communityForm: CreateCommunityForm): Promise<void> => {
    const isBusiness = process.env.REACT_APP_APPLICATION_VERSION === "BUSINESS";
    const source = isBusiness ? 'business-dashboard' : 'community-dashboard'
    const { data: community } = await httpService.post<Community>(
      `/networks/communities?source=${source}`,
      communityForm,
    );
    store.dispatch(addCommunity(community));
    if (community.id) store.dispatch(setSelectedCommunity(community.id));
    if (community.dashboard?.dashboardId)
      store.dispatch(setSelectedDashboard(community.dashboard?.dashboardId));
  },

  createRelatedCommunity: async (businessId: number, communityForm: CreateCommunityForm): Promise<void> => {
    const { data: community } = await httpService.post<Community>(
      `/networks/businesses/${businessId}/communities`,
      communityForm,
      {
        params: {
          origin: 'business-dashboard',
        },
      },
    );
    store.dispatch(addCommunity(community));
  },

  fetchCommunity: async (): Promise<void> => {
    const selectedCommunity = getSelectedCommunity();
    return httpService.get<Community[]>('/networks/communities/@me').then(({ data }): void => {
      const communities = data.map((b) =>
        b.id === selectedCommunity?.id
          ? { ...b, inviteFields: selectedCommunity?.inviteFields }
          : b,
      );
      store.dispatch(setState({ ...store.getState().application, communities }));
    });
  },

  fetchCommunitiesForBusiness: async (businessId: number): Promise<Community[]> => {
    const { data } = await httpService.get<Community[]>(`/networks/businesses/${businessId}/communities`);
    return data;
  },

  deleteCommunity: async (): Promise<void> => {
    const communityId = store.getState().application.selectedCommunity;
    await httpService.delete(`/networks/communities/${communityId}`);
    const id = store.getState().application.selectedCommunity;
    store.dispatch(wipeSelectedCommunity());
    store.dispatch(deleteCommunity(id));
  },

  updateCommunity: async (
    name: string,
    description: string,
    memberPermissions: CommunityMemberPermissions,
    features: CommunityFeatures,
    communityId?: number,
  ): Promise<void> => {
    const resolved = communityId ?? store.getState().application.selectedCommunity;
    await httpService.put(`/networks/communities/${resolved}`, {
      name,
      description,
      connectedMembers: String(features.EASY_SHARING),
      features,
      memberPermissions,
    });

    const { application } = store.getState();
    const { communities } = application;
    if (communities?.some((c: Community) => c.id === resolved)) {
      store.dispatch(
        setState({
          ...application,
          communities: communities.map((c: Community) =>
            c.id === resolved ? { ...c, name, description, features, memberPermissions } : c,
          ),
        }),
      );
    }
  },

  uploadPicture: (file: File): Promise<UploadedPictureModel> => {
    const communityId = store.getState().application.selectedCommunity;
    const formData = new FormData();
    formData.append('picture', file);
    return httpService
      .post<UploadedPictureModel>(`/networks/communities/${communityId}/cover-photos`, formData)
      .then(({ data }) => data);
  },

  getPicturesForCommunity: (): Promise<UploadedPictureModel[]> => {
    const communityId = store.getState().application.selectedCommunity;
    return httpService
      .get<UploadedPictureModel[]>(`/networks/communities/${communityId}/cover-photos`)
      .then(({ data }) => data);
  },

  deletePicture: (id: number): Promise<void> => {
    const communityId = store.getState().application.selectedCommunity;
    return httpService.delete(`/networks/communities/${communityId}/cover-photos/${id}`);
  },

  activateDashboard: async (
    communityId: number,
    features: CommunityFeatures,
    trialType: SubscriptionType,
  ): Promise<MemberCommunityDto> => {
    return httpService
      .post<MemberCommunityDto>(`/networks/communities/${communityId}/dashboard`, {
        trialType,
        features,
      })
      .then(({ data }) => {
        const { application } = store.getState() as RootState;
        store.dispatch(
          setState({
            ...application,
            communities: application.communities?.map((b) =>
              b.id === communityId
                ? { ...b, dashboard: { dashboardId: data.dashboard!.dashboardId }, features }
                : b,
            ),
          }),
        );
        return data;
      });
  },

  addMembersDirectly: async (
    communityId: number,
    userIds: (number | string)[],
    role: CommunityRole = CommunityRole.MEMBER,
  ): Promise<any[]> => {
    if (!userIds || userIds.length === 0) return [];

    return Promise.all(
      userIds
        .map((userId) => Number(userId))
        .filter((userId) => Number.isFinite(userId) && userId > 0)
        .map((userId) =>
          httpService.post(`/networks/communities/${communityId}/members`, {
            userId,
            role,
          }),
        ),
    );
  },

  getCommunityMembers: async (communityId: number): Promise<Connection[]> => {
    const [{ data: members }, externalMembers] = await Promise.all([
      httpService.get<any[]>(`/networks/communities/${communityId}/members/`),
      httpService
        .get<any[]>(`/networks/communities/${communityId}/members/external`)
        .then((res) => res.data)
        .catch(() => [] as any[]),
    ]);
    const data = [...members, ...externalMembers];

    return data.map((member) => ({
      dashboardMemberId: member.dashboardMemberId || member.id || String(member.memberId),
      memberId: member.memberId,
      userId: member.userId,
      externalMemberId: member.externalMemberId,
      parentExternalMemberId: member.parentExternalMemberId,
      fields: {
        FIRST_NAME: member.firstName || member.fields?.FIRST_NAME || '',
        LAST_NAME: member.lastName || member.fields?.LAST_NAME || '',
        EMAIL: member.email || member.fields?.EMAIL || '',
        PHONENUMBER: member.phoneNumber || member.fields?.PHONENUMBER || '',
        ADDRESS: member.address || member.fields?.ADDRESS || '',
        COMMUNICATION_NAME: member.communicationName || member.fields?.COMMUNICATION_NAME || '',
        ...(member.fields || {}),
      },
      tags: member.tags || [],
      type: member.type || ConnectionType.MEMBER,
      note: member.note || '',
      verificationLabel: member.verificationLabel || '',
      active: member.active !== undefined ? member.active : true,
      inactiveReason: member.inactiveReason,
      inviteSent: member.inviteSent,
      picture: member.picture,
    }));
  },
};

export default communityService;
